import { uniqBy } from 'lodash-es'
import { prefectureDisplayName } from '../prefecture/prefecture'

export type InternalLink = {
  canonicalPath: string
  anchorText: string
}

type RestaurantAreaInput = {
  largeName: string
  largeCode: string
  mediumName: string
  mediumKey: string
  mediumCode: string
  smallName: string
  smallCode: string
  minimums: {
    code: string
    name: string
  }[]
}

type RestaurantGenreInput = {
  major: {
    code: string
    name: string
  }
  minor: {
    code: string
    name: string
  }
}

type RelatedTagsInput = Tag[]

export function buildSimilarRestaurantLinks(input: {
  area: RestaurantAreaInput
  genre: RestaurantGenreInput
  relatedTags: RelatedTagsInput
}): InternalLink[] {
  const areas = buildAreas(input.area)
  const genres = buildGenres(input.genre)
  const tags = input.relatedTags

  return areaAndSceneTagLinks(areas, tags)
    .concat(areaAndGenreAndSceneTagLinks(areas, genres, tags))
    .concat(areaLinks(areas))
    .concat(areaAndGenreLinks(areas, genres))
    .concat(areaAndMealPeriodLinks(areas))
    .concat(areaAndGenreAndMealPeriodLinks(areas, genres))
    .concat(areaAndFeaturedTagLinks(areas, tags))
}

type Area = MediumArea | SmallArea | MinimumArea

type MediumArea = {
  key: string
  code: string
  name: string
  type: 'medium'
}

type SmallArea = {
  code: string
  mediumKey: string
  name: string
  type: 'small'
}

type MinimumArea = {
  code: string
  mediumKey: string
  smallCode: string
  name: string
  type: 'minimum'
}

type MealPeriod = {
  code: string
  name: string
  type: string
}

const mealPeriods: MealPeriod[] = [
  {
    code: '03',
    name: 'ランチ',
    type: 'lunch',
  },
  {
    code: '05',
    name: 'ディナー',
    type: 'dinner',
  },
]

type Genre = {
  code: string
  name: string
  type: 'major' | 'minor'
}

type Tag = {
  type: 'DRINK' | 'FOOD' | 'GENRE' | 'SCENE'
  tagId: string
  name: string
}

function buildAreas(input: RestaurantAreaInput): Area[] {
  const mediumArea: MediumArea = {
    key: input.mediumKey,
    code: input.mediumCode,
    name: prefectureDisplayName(input.mediumName),
    type: 'medium',
  }

  const smallOrMinimumArea: SmallArea | MinimumArea =
    input.minimums.length > 0
      ? {
          code: input.minimums[0].code,
          mediumKey: input.mediumKey,
          smallCode: input.smallCode,
          name: input.minimums[0].name,
          type: 'minimum',
        }
      : {
          code: input.smallCode,
          mediumKey: input.mediumKey,
          name: input.smallName,
          type: 'small',
        }

  // 表示上同じ名前になるエリアは上位のエリアだけを残す
  // cf. https://github.com/ikyucom/restaurant2/blob/ff6a3763ff1197ad359b4e6d08673034563ed9f9/restaurant/view_models/similar_link.py#L127
  return uniqBy([mediumArea, smallOrMinimumArea], (area) => area.name)
}

const OTHER_MAJOR_GENRE_CODE = '90000'

function buildGenres(input: RestaurantGenreInput): Genre[] {
  let result: Genre[] = []

  // 「その他」のジャンルは内部リンク生成に利用しない
  // cf. https://github.com/ikyucom/restaurant2/blob/d998a5fb1b0ca7c5fa42bae22992eb562add4ef0/restaurant/view_models/similar_link.py#L131
  if (input.major.code != OTHER_MAJOR_GENRE_CODE) {
    result.push({
      code: input.major.code,
      name: input.major.name,
      type: 'major',
    })
  }

  result.push({
    code: input.minor.code,
    name: input.minor.name,
    type: 'minor',
  })

  return result
}

function areaLinks(areas: Area[]): InternalLink[] {
  return areas.map((area) => {
    switch (area.type) {
      case 'medium':
        return {
          canonicalPath: `/area/${area.key}/`,
          anchorText: area.name,
        }
      case 'small':
        // 小エリアのサブトップページが存在しないので、極小エリアを持たない店舗のときは
        // アンカーテキストが小エリア名だがリンク先は中エリアのサブトップであるようなリンクを生成する
        // cf. https://github.com/ikyucom/restaurant2/blob/ff6a3763ff1197ad359b4e6d08673034563ed9f9/restaurant/view_models/similar_link.py#L91
        return {
          canonicalPath: `/area/${area.mediumKey}/${area.code}/`,
          anchorText: area.name,
        }
      case 'minimum':
        return {
          canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/`,
          anchorText: area.name,
        }
      default:
        throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
    }
  })
}

function areaAndMealPeriodLinks(areas: Area[]): InternalLink[] {
  return areas.flatMap((area) => {
    return mealPeriods.map((period) => {
      switch (area.type) {
        case 'medium':
          return {
            canonicalPath: `/area/${area.key}/${period.code}/`,
            anchorText: `${area.name} ${period.name}`,
          }
        case 'small':
          // 小エリアのサブトップページが存在しないので、極小エリアを持たない店舗のときは
          // アンカーテキストが小エリア名だがリンク先は中エリアのサブトップであるようなリンクを生成する
          // cf. https://github.com/ikyucom/restaurant2/blob/ff6a3763ff1197ad359b4e6d08673034563ed9f9/restaurant/view_models/similar_link.py#L91
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.code}/${period.code}/`,
            anchorText: `${area.name} ${period.name}`,
          }
        case 'minimum':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/${period.code}/`,
            anchorText: `${area.name} ${period.name}`,
          }
        default:
          throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
      }
    })
  })
}

function areaAndGenreLinks(areas: Area[], genres: Genre[]): InternalLink[] {
  return areas.flatMap((area) => {
    return genres.map((genre) => {
      switch (area.type) {
        case 'medium':
          return {
            canonicalPath: `/area/${area.key}/${genre.code}/`,
            anchorText: `${area.name} ${genre.name}`,
          }
        case 'small':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.code}/${genre.code}/`,
            anchorText: `${area.name} ${genre.name}`,
          }
        case 'minimum':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/${genre.code}/`,
            anchorText: `${area.name} ${genre.name}`,
          }
        default:
          throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
      }
    })
  })
}

function areaAndGenreAndMealPeriodLinks(areas: Area[], genres: Genre[]): InternalLink[] {
  return areas.flatMap((area) => {
    return genres.flatMap((genre) => {
      return mealPeriods.map((period) => {
        switch (area.type) {
          case 'medium':
            return {
              canonicalPath: `/area/${area.key}/${genre.code}/${period.code}/`,
              anchorText: `${area.name} ${genre.name} ${period.name}`,
            }
          case 'small':
            return {
              canonicalPath: `/area/${area.mediumKey}/${area.code}/${genre.code}/${period.code}/`,
              anchorText: `${area.name} ${genre.name} ${period.name}`,
            }
          case 'minimum':
            return {
              canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/${genre.code}/${period.code}/`,
              anchorText: `${area.name} ${genre.name} ${period.name}`,
            }
          default:
            throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
        }
      })
    })
  })
}

const NIGHT_VIEW_TAG_ID = '366'

function tag_segment(tag: Tag): string {
  if (tag.type === 'SCENE' && tag.tagId === NIGHT_VIEW_TAG_ID) {
    // 「夜景が綺麗」のタグのURLは必ずディナーの時間帯(05)に限定する
    return `05/t-${tag.type.toLowerCase()}${tag.tagId}`
  } else {
    return `t-${tag.type.toLowerCase()}${tag.tagId}`
  }
}

function areaAndSceneTagLinks(areas: Area[], tags: Tag[]): InternalLink[] {
  const sceneTags = tags.filter((tag) => tag.type == 'SCENE')

  return areas.flatMap((area) => {
    return sceneTags.map((tag) => {
      switch (area.type) {
        case 'medium':
          return {
            canonicalPath: `/area/${area.key}/${tag_segment(tag)}/`,
            anchorText: `${area.name} ${tag.name}`,
          }
        case 'small':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.code}/${tag_segment(tag)}/`,
            anchorText: `${area.name} ${tag.name}`,
          }
        case 'minimum':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/${tag_segment(
              tag
            )}/`,
            anchorText: `${area.name} ${tag.name}`,
          }
        default:
          throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
      }
    })
  })
}

function areaAndGenreAndSceneTagLinks(areas: Area[], genres: Genre[], tags: Tag[]): InternalLink[] {
  const sceneTags = tags.filter((tag) => tag.type == 'SCENE')

  return areas.flatMap((area) => {
    return genres.flatMap((genre) => {
      return sceneTags.map((tag) => {
        switch (area.type) {
          case 'medium':
            return {
              canonicalPath: `/area/${area.key}/${genre.code}/${tag_segment(tag)}/`,
              anchorText: `${area.name} ${genre.name} ${tag.name}`,
            }
          case 'small':
            return {
              canonicalPath: `/area/${area.mediumKey}/${area.code}/${genre.code}/${tag_segment(
                tag
              )}/`,
              anchorText: `${area.name} ${genre.name} ${tag.name}`,
            }
          case 'minimum':
            return {
              canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/${
                genre.code
              }/${tag_segment(tag)}/`,
              anchorText: `${area.name} ${genre.name} ${tag.name}`,
            }
          default:
            throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
        }
      })
    })
  })
}

function areaAndFeaturedTagLinks(areas: Area[], tags: Tag[]): InternalLink[] {
  const featuredTags = tags.filter(
    (tag) => tag.type == 'DRINK' || tag.type == 'FOOD' || tag.type == 'GENRE'
  )

  return areas.flatMap((area) => {
    return featuredTags.map((tag) => {
      switch (area.type) {
        case 'medium':
          return {
            canonicalPath: `/area/${area.key}/${tag_segment(tag)}/`,
            anchorText: `${area.name} ${tag.name}`,
          }
        case 'small':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.code}/${tag_segment(tag)}/`,
            anchorText: `${area.name} ${tag.name}`,
          }
        case 'minimum':
          return {
            canonicalPath: `/area/${area.mediumKey}/${area.smallCode}/${area.code}/${tag_segment(
              tag
            )}/`,
            anchorText: `${area.name} ${tag.name}`,
          }
        default:
          throw new Error(`Unknown area type: ${(area as { type: '__invalid__' }).type}`)
      }
    })
  })
}
